import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ArrowUpRight, ArrowDownRight, TrendingUp, HelpCircle, AlertCircle } from 'lucide-react';
import { FadeIn } from '@/components/motion';
import { formatNumber, formatRelativeTime } from '@/lib/utils/format';
import { dashboardService } from '../services/dashboard-service';
import { Sparkline } from './sparkline';
import type { ExchangeSummaryResponse } from '../types/exchange-rate.types';

export function ExchangeRateWidget() {
  const [data, setData] = useState<ExchangeSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dashboardService
      .getExchangeSummary()
      .then(setData)
      .catch((err) => setError(err?.message || 'Error al cargar tipo de cambio'))
      .finally(() => setLoading(false));
  }, []);

  if (!loading && !data && !error) return null;

  return (
    <FadeIn delay={0.5}>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                Tipo de Cambio P2P
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    Tasas USDT/BOB y USDC/BOB obtenidas de exchanges P2P (Binance, Bybit, Bitget)
                    via CriptoYa. Se actualizan automáticamente cada 30 minutos.
                  </TooltipContent>
                </Tooltip>
              </CardTitle>
              <CardDescription>
                {data?.lastSync
                  ? `Actualizado ${formatRelativeTime(data.lastSync)}`
                  : 'BOB — CriptoYa'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? (
            <div className="flex items-center gap-2 text-destructive text-sm">
              <AlertCircle className="w-4 h-4" />
              <p>{error}</p>
            </div>
          ) : loading ? (
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : (
            data?.summaries.map((summary) => {
              if (!summary.currentRate) return null;
              const isPositive = summary.variation24h !== null && summary.variation24h >= 0;
              const sparkColor = isPositive ? '#10b981' : '#ef4444';

              return (
                <div key={summary.coin} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{summary.coin}/BOB</span>
                      {summary.variation24h !== null && (
                        <Badge
                          variant={isPositive ? 'success' : 'destructive'}
                          className="text-xs px-1.5 py-0"
                        >
                          {isPositive ? (
                            <ArrowUpRight className="w-3 h-3 mr-0.5 inline" />
                          ) : (
                            <ArrowDownRight className="w-3 h-3 mr-0.5 inline" />
                          )}
                          {Math.abs(summary.variation24h).toFixed(2)}%
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {summary.history.length > 1 && (
                        <Sparkline
                          data={summary.history}
                          color={sparkColor}
                          width={80}
                          height={24}
                        />
                      )}
                      <span className="text-lg font-bold tabular-nums">
                        {formatNumber(summary.currentRate, 4)}
                      </span>
                    </div>
                  </div>

                  {summary.exchanges.length > 0 && (
                    <div className="grid grid-cols-3 gap-1 text-xs">
                      {summary.exchanges.map((ex) => (
                        <Tooltip key={ex.name}>
                          <TooltipTrigger asChild>
                            <div className="bg-muted/50 rounded px-2 py-1 cursor-help">
                              <p className="text-muted-foreground truncate">{ex.name}</p>
                              <p className="font-mono">
                                <span className="text-ok">{formatNumber(ex.bid, 2)}</span>
                                {' / '}
                                <span className="text-bloqueo">{formatNumber(ex.ask, 2)}</span>
                              </p>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            Compra (verde): precio al que compras BOB. Venta (rojo): precio al que
                            vendes BOB.
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </FadeIn>
  );
}

export default ExchangeRateWidget;
